import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { deleteAddresses, getGetDeviceGroupByIDKey, getGetDeviceGroupsKey } from 'gen/client';
import { useCallback, useMemo, useState } from 'react';
import { useSWRConfig } from 'swr';

import { useRenkiConfig } from '@ui/Utils/device/installationMetadata';

import CustomDatagrid from 'kerberus-components/src/Components/DataGrid/CustomDataGrid';
import { dayjs } from 'kerberus-components/src/Components/Dayjs/dayjs-utils';
import ConfirmationDialog from 'kerberus-components/src/Components/Modals/ConfirmationDialog';
import { UIAddress } from 'kerberus-components/src/Types/WizardSetup';

import type { UserUIDeviceGroupResponseDTO } from 'gen/model';

const deleteContent = <div>This will delete device ID</div>;

interface DataGridMacAddressProps {
    deviceData: UserUIDeviceGroupResponseDTO;
}

interface AddressesWithActivity extends UIAddress {
    created_at?: string | number | Date | null;
    site_name?: string;
    timestamp?: string;
}

export default function DataGridMacAddress({ deviceData }: Readonly<DataGridMacAddressProps>) {
    const [deleteAddress, setDeleteAddress] = useState('');
    const [deleteOpen, setDeleteOpen] = useState(false);
    const { mutate } = useSWRConfig();

    const { config, updateConfig } = useRenkiConfig();

    const rows = deviceData.addresses;

    const columns: GridColDef<AddressesWithActivity>[] = useMemo(
        () => [
            {
                field: 'created_at',
                headerName: 'Created at',
                minWidth: 135,
                renderCell: (params) =>
                    params.row.created_at ? dayjs(params.row.created_at).format('DD.MM.YYYY HH:mm') : 'Unknown',
            },
            { field: 'instance_id', headerName: 'Place', minWidth: 110 },
            { field: 'address', flex: 1, headerName: 'Device ID', minWidth: 135 },
            {
                align: 'center',
                field: 'Delete',
                headerAlign: 'center',
                headerName: 'Delete',
                renderCell: ({ row }) => (
                    <IconButton
                        onClick={() => {
                            setDeleteAddress(row.address);
                            setDeleteOpen(true);
                        }}
                    >
                        <DeleteIcon color="error" />
                    </IconButton>
                ),
                width: 120,
            },
        ],
        [],
    );

    const handleAddressRemove = useCallback(async () => {
        if (!deleteAddress) {
            return;
        }
        if (!deviceData.id) {
            return;
        }
        try {
            const body = { net: [deleteAddress] };
            await deleteAddresses(deviceData.id, {
                addresses: body.net.map((e) => ({
                    address: atob(e),
                    bluetooth: false,
                })),
            });

            if (config?.discoverIgnore) {
                void updateConfig({ discoverIgnore: undefined });
            }
            void mutate(getGetDeviceGroupByIDKey(deviceData.id));
            void mutate(getGetDeviceGroupsKey());
        } catch (error) {
            alert(error);
        }
    }, [config, deleteAddress, deviceData.id, mutate, updateConfig]);

    return (
        <>
            {rows.length > 0 ? (
                <CustomDatagrid
                    columns={columns}
                    density="compact"
                    getRowId={(row: AddressesWithActivity) => `${row.bluetooth ? 'bl' : 'net'}-${row.address}`}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 10,
                            },
                        },
                    }}
                    pageSizeOptions={[5, 10, 25, 50, 100]}
                    rows={rows.map((row) => ({ ...row, address: btoa(row.address!) }))}
                />
            ) : (
                <Stack
                    sx={{
                        alignItems: 'center',
                        height: 100,
                        justifyContent: 'center',
                        px: 2,
                    }}
                >
                    <Typography
                        sx={{
                            textAlign: 'center',
                        }}
                    >
                        No device details found. Please update device details or add a new device.
                    </Typography>
                </Stack>
            )}
            <ConfirmationDialog
                content={deleteContent}
                handleDevice={handleAddressRemove}
                name={deleteAddress}
                open={deleteOpen}
                setOpen={setDeleteOpen}
                title={['Delete']}
            />
        </>
    );
}
